/**
 * Refunds.
 *
 * The page opens on the work, not on a search box. Most days nobody is looking
 * up a wallet -- somebody is waiting for a decision, or a refund is in flight
 * and the question is whether it is stuck. Those are the first two tabs;
 * issuing a refund by hand is a thing you go and do, so it lives behind one.
 *
 * Everything needed to decide is on the card. Sending a reviewer to another
 * screen to work out whether ten rupees is really available is how the wrong
 * button gets pressed.
 */
import React, { useEffect, useMemo, useState } from "react";
import MaintenanceLayout from "../shared/MaintenanceLayout";
import { isSuperAdmin } from "../shared/MaintenanceGuard";
import "./refunds.scss";
import { useNotice } from "../shared/useNotice";
import {
  inr, dt, time, ago, statusStory, auditSentence, journeyOf,
  customerMessage, drawReceipt,
} from "./story";
import {
  WalletLookup, RefundLot, RefundRow, RefundDetail, SourceRefundResult,
  PendingReview, AuditRow, RefundSettings,
  lookupWallet, listRefunds, sourceRefund, settleRefund, getRefund, refreshRefund,
  reviewQueue, refundAudit, approveRefund, rejectRefund,
  getRefundSettings, putRefundSettings,
} from "../../../api/refunds";

const REASONS = [
  ["customer-request", "Customer asked for their money back"],
  ["service-failure", "Something we did wrong"],
  ["duplicate-payment", "Paid twice"],
  ["account-closure", "Closing the account"],
  ["goodwill", "Goodwill"],
  ["other", "Other"],
];

type Tab = "queue" | "flight" | "history" | "issue" | "settings";

/* ------------------------------------------------------------------ pieces */

const Badge: React.FC<{ status: string }> = ({ status }) => {
  const s = statusStory(status);
  return <span className={`rf-badge ${s.tone}`}>{s.label}</span>;
};

const Journey: React.FC<{ status: string; raisedByUser: boolean }> = ({
  status, raisedByUser,
}) => {
  const { steps, at, broken } = journeyOf(status, raisedByUser);
  return (
    <div className="rf-journey">
      {steps.map((label, i) => (
        <React.Fragment key={label}>
          {i > 0 && <span className={`rf-journey__bar ${i <= at ? "done" : ""}`} />}
          <span
            className={`rf-journey__step ${
              i < at ? "done" : i === at ? (broken ? "broken" : "now") : ""
            }`}
          >
            <span className="rf-journey__dot">
              {broken && i === at ? "!" : i < at ? "✓" : i + 1}
            </span>
            {label}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
};

const Figures: React.FC<{ rows: Array<[string, React.ReactNode, string?]> }> = ({
  rows,
}) => (
  <div className="rf-figures">
    {rows.map(([k, v, cls]) => (
      <div key={k} className="rf-figures__row">
        <span>{k}</span>
        <b className={cls}>{v}</b>
      </div>
    ))}
  </div>
);

const Empty: React.FC<{ icon: string; title: string; body: React.ReactNode }> = ({
  icon, title, body,
}) => (
  <div className="rf-empty">
    <div className="rf-empty__icon">{icon}</div>
    <h3>{title}</h3>
    <p>{body}</p>
  </div>
);

/* ------------------------------------------------------- customer handout */

/**
 * The thing support actually needs: something to send the person who is owed
 * money. Written once, here, so two agents answering the same question do not
 * give the customer two different stories.
 */
const CustomerHandout: React.FC<{ d: RefundDetail }> = ({ d }) => {
  const copy = customerMessage(d);
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(copy.body);
      setState("copied");
      window.setTimeout(() => setState("idle"), 2500);
    } catch {
      setState("failed");
    }
  };

  const savePng = async () => {
    const blob = await drawReceipt(d);
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `refund-${d.id}-${(d.who ?? "customer").replace(/\W+/g, "-").toLowerCase()}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rf-handout">
      <div className="rf-handout__head">
        <h4 className="rf-h4">Send to the customer</h4>
        {!copy.sendable && (
          <span className="rf-handout__warn">needs editing before you send it</span>
        )}
      </div>
      <p className="rf-muted">
        Subject: <b>{copy.subject}</b>
      </p>
      <textarea className="rf-handout__text" readOnly rows={12} value={copy.body} />
      <div className="rf-actions">
        <button className="rf-primary" onClick={copyText}>
          {state === "copied" ? "Copied ✓" : state === "failed" ? "Select and copy manually" : "Copy message"}
        </button>
        <button onClick={savePng}>Download as image</button>
      </div>
      <p className="rf-hint">
        Both carry only what a customer should see — the bank reference, the
        amount and the timing. Our internal reference, the wallet id and the
        gateway's own words stay out of anything that can be forwarded.
      </p>
    </div>
  );
};

/* ------------------------------------------------------------- review card */

/**
 * One decision, with the arithmetic already done.
 *
 * A reviewer is deciding against what the customer was promised, so the promise
 * leads. The wallet as it stands now only gets its own line when it disagrees
 * with that promise -- two identical columns just ask the reader to compare
 * them for no reason.
 */
const ReviewCard: React.FC<{
  r: PendingReview;
  mayAct: boolean;
  busy: boolean;
  onApprove: () => void;
  onDecline: (why: string) => void;
}> = ({ r, mayAct, busy, onApprove, onDecline }) => {
  const [declining, setDeclining] = useState(false);
  const [why, setWhy] = useState("");

  const shown = r.quote_shown ?? {};
  const balanceThen = Number(shown.balance ?? NaN);
  const movedBalance =
    Number.isFinite(balanceThen) && Math.abs(balanceThen - r.current_balance) >= 0.01;
  const stillCovered = r.cash_balance + 0.001 >= r.amount;
  const limitedBy: string | undefined = shown.quote?.limited_by;

  return (
    <article className="rf-review">
      <header className="rf-review__head">
        <div>
          <h3>{r.who ?? `Wallet ${r.user_wallet_id}`}</h3>
          <p className="rf-review__sub">
            {r.phone ?? "no phone on file"} · wallet {r.user_wallet_id} · asked{" "}
            {ago(r.created_at)}
          </p>
        </div>
        <div className="rf-review__amount">
          <span>they want back</span>
          <b>{inr(r.amount)}</b>
        </div>
      </header>

      <p className="rf-review__story">
        They paid this in on <b>{dt(r.lot_paid_at)}</b>
        {r.upi_id ? <> by UPI to <b>{r.upi_id}</b></> : null}
        {" — "}
        {r.lot_age_days === 0 ? "today" : `${r.lot_age_days} days ago`}. That
        recharge was {inr(r.lot_amount)}
        {limitedBy ? <>, and the amount is capped by <b>{limitedBy}</b></> : null}.
      </p>

      {r.reason_note ? (
        <blockquote className="rf-said">“{r.reason_note}”</blockquote>
      ) : null}

      <div className="rf-outcome">
        <span className="rf-outcome__label">If you approve</span>
        <ul>
          <li>
            <b>{inr(r.amount)}</b> goes back to{" "}
            <b>{r.upi_id ?? "the way they paid"}</b>
          </li>
          {r.clawback_amount > 0 ? (
            <li>
              They lose <b className="promo">{inr(r.clawback_amount)}</b> of
              cashback — a bonus is forfeited in full on any refund
            </li>
          ) : (
            <li>No cashback is lost</li>
          )}
          <li>
            Their wallet goes from <b>{inr(r.current_balance)}</b> to{" "}
            <b>{inr(r.current_balance - r.amount - r.clawback_amount)}</b>
          </li>
          <li className="rf-outcome__slow">
            Their bank shows it in 7–10 working days, not instantly
          </li>
        </ul>
      </div>

      {movedBalance ? (
        <p className={`rf-flag ${stillCovered ? "warn" : "bad"}`}>
          {stillCovered ? "⚠" : "✕"} Their balance has changed since they asked —{" "}
          {inr(balanceThen)} then, {inr(r.current_balance)} now.{" "}
          {stillCovered
            ? `The ${inr(r.amount)} is still covered by their own money.`
            : `Only ${inr(r.cash_balance)} of their own money is left, so this no longer covers it — declining and asking them to raise it again is the safe move.`}
        </p>
      ) : (
        <p className="rf-flag ok">
          ✓ Nothing has changed since they asked — the figures they were shown
          still hold.
        </p>
      )}

      {mayAct ? (
        declining ? (
          <div className="rf-decline">
            <label>
              Why are you declining? Kept on the record, and the customer is told
              nothing moved.
              <input
                autoFocus
                value={why}
                onChange={(e) => setWhy(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && why.trim() && onDecline(why.trim())}
                placeholder="e.g. duplicate of the request they raised yesterday"
              />
            </label>
            <div className="rf-actions">
              <button className="bad" disabled={busy || !why.trim()} onClick={() => onDecline(why.trim())}>
                {busy ? "…" : "Decline this request"}
              </button>
              <button className="ghost" onClick={() => setDeclining(false)}>
                keep it open
              </button>
            </div>
          </div>
        ) : (
          <div className="rf-actions">
            <button className="rf-primary" disabled={busy} onClick={onApprove}>
              {busy ? "Sending…" : `Approve — send ${inr(r.amount)} back`}
            </button>
            <button disabled={busy} onClick={() => setDeclining(true)}>Decline</button>
          </div>
        )
      ) : (
        <p className="rf-muted">Read-only — a super-admin decides refunds.</p>
      )}
    </article>
  );
};

/* ----------------------------------------------------------------- detail */

const DetailDrawer: React.FC<{
  d: RefundDetail;
  audit: AuditRow[];
  mayAct: boolean;
  checking: boolean;
  message: string | null;
  onCheck: () => void;
  onOverride: (o: "succeeded" | "failed") => void;
  onClose: () => void;
}> = ({ d, audit, mayAct, checking, message, onCheck, onOverride, onClose }) => {
  const [tech, setTech] = useState(false);
  const [override, setOverride] = useState(false);
  const s = statusStory(d.status);
  const raisedByUser = (d as any).raised_by === "user";

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  return (
    <div className="rf-drawer" role="dialog" aria-modal="true">
      <div className="rf-drawer__scrim" onClick={onClose} />
      <div className="rf-drawer__panel">
        <header className="rf-drawer__head">
          <div>
            <span className="rf-muted">Refund #{d.id}</span>
            <h2>{inr(d.amount)} to {d.who ?? `wallet ${d.user_wallet_id}`}</h2>
          </div>
          <button className="rf-x" onClick={onClose} aria-label="Close">✕</button>
        </header>

        <div className={`rf-verdict ${s.tone}`}>
          <b>{s.label}</b>
          <p>{s.meaning}</p>
          <p className="rf-verdict__next">{s.next}</p>
        </div>

        <Journey status={d.status} raisedByUser={raisedByUser} />

        {message && <p className="rf-hint rf-hint--loud">{message}</p>}

        <div className="rf-actions">
          <button onClick={onCheck} disabled={checking}>
            {checking ? "Asking Cashfree…" : "Ask Cashfree where it is"}
          </button>
        </div>

        <CustomerHandout d={d} />

        <h4 className="rf-h4">What happened</h4>
        <ol className="rf-timeline">
          {audit.map((a) => {
            const { text, tone } = auditSentence(a);
            return (
              <li key={a.id} className={tone}>
                <span className="rf-timeline__when">{time(a.at)}</span>
                <span className="rf-timeline__dot" />
                <span className="rf-timeline__what">
                  {text}
                  <span className="rf-timeline__date">{dt(a.at)}</span>
                </span>
              </li>
            );
          })}
          {audit.length === 0 && (
            <li className="muted">
              <span className="rf-timeline__when">—</span>
              <span className="rf-timeline__dot" />
              <span className="rf-timeline__what">
                No trail recorded. Refunds raised before the audit table existed
                have none.
              </span>
            </li>
          )}
        </ol>

        <h4 className="rf-h4">The money</h4>
        <Figures
          rows={[
            ["Refunded", inr(d.amount)],
            ...(d.clawback_amount > 0
              ? ([["Cashback forfeited", inr(d.clawback_amount), "promo"]] as Array<
                  [string, React.ReactNode, string]
                >)
              : []),
            ["Back to", d.upi_id || d.payment_group || "the original method"],
            [
              "Bank reference (ARN)",
              d.refund_arn ?? (d.status === "succeeded" ? "—" : "issued once it settles"),
            ],
            ["From the recharge of", `${inr(d.lot_amount)} paid ${dt(d.lot_paid_at)}`],
            ["Wallet now", inr(d.current_balance)],
          ]}
        />

        <button className="rf-disclose" onClick={() => setTech((t) => !t)}>
          {tech ? "▾" : "▸"} Technical detail
        </button>
        {tech && (
          <>
            <Figures
              rows={[
                ["Our reference", d.pg_refund_id ?? "—"],
                ["Cashfree order", d.our_order_id ?? "—"],
                ["Gateway status", d.pg_status ?? "—"],
                ["Processed at gateway", dt(d.gateway_processed_at)],
                ["Raised", `${dt(d.created_at)} by ${d.performed_by ?? "—"}`],
                ["Settled", dt(d.settled_at)],
                ["Reason", `${d.reason_code ?? "—"}${d.reason_note ? ` — ${d.reason_note}` : ""}`],
                ...(d.failure_detail
                  ? ([["Failure", d.failure_detail]] as Array<[string, React.ReactNode]>)
                  : []),
              ]}
            />
            <h4 className="rf-h4">Ledger movements</h4>
            <div className="rf-scroll">
              <table className="rf-table">
                <thead>
                  <tr>
                    <th>Txn</th><th>Type</th><th>Amount</th>
                    <th>Cash</th><th>Cashback</th><th>Wallet</th>
                  </tr>
                </thead>
                <tbody>
                  {d.legs.map((l) => (
                    <tr key={l.id}>
                      <td>{l.id}</td>
                      <td>{l.type}</td>
                      <td>{inr(l.amount)}</td>
                      <td>{l.cash_amount === null ? "—" : inr(l.cash_amount)}</td>
                      <td>{l.promo_amount === null ? "—" : inr(l.promo_amount)}</td>
                      <td>{inr(l.wallet_balance_before)} → {inr(l.wallet_balance_after)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="rf-hint">
              A refund leaves as cash and a forfeited bonus leaves as cashback, so
              promotional credit can never be paid out as money.
            </p>
          </>
        )}

        {mayAct && d.status === "processing" && (
          <div className="rf-danger-zone">
            <button className="rf-disclose" onClick={() => setOverride((o) => !o)}>
              {override ? "▾" : "▸"} Override the outcome
            </button>
            {override && (
              <>
                <p className="rf-hint">
                  This records an outcome <b>without asking Cashfree</b>. It moves
                  no money and tells the customer nothing — it only changes what
                  this console believes. Use “Ask Cashfree where it is” first.
                  This is for when the gateway cannot be reached at all and
                  someone has confirmed the outcome another way.
                </p>
                <div className="rf-actions">
                  <button className="ok" onClick={() => onOverride("succeeded")}>
                    Record as paid
                  </button>
                  <button className="bad" onClick={() => onOverride("failed")}>
                    Record as rejected
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------- page */

const RefundConsole: React.FC = () => {
  const mayAct = isSuperAdmin();

  const [tab, setTab] = useState<Tab>("queue");
  const [rows, setRows] = useState<RefundRow[]>([]);
  const [queue, setQueue] = useState<PendingReview[]>([]);
  const [cfg, setCfg] = useState<RefundSettings | null>(null);
  const [err, setErr] = useNotice("error", null);
  const [flash, setFlash] = useState<string | null>(null);
  const [acting, setActing] = useState<number | null>(null);

  const [detail, setDetail] = useState<RefundDetail | null>(null);
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [checking, setChecking] = useState(false);
  const [checkMsg, setCheckMsg] = useState<string | null>(null);

  const refreshList = () => listRefunds().then(setRows).catch(() => {});
  const refreshQueue = () => reviewQueue().then(setQueue).catch(() => {});
  useEffect(() => {
    refreshList();
    refreshQueue();
    getRefundSettings().then(setCfg).catch(() => {});
  }, []);

  const inFlight = useMemo(() => rows.filter((r) => r.status === "processing"), [rows]);
  const stuck = useMemo(() => rows.filter((r) => r.status === "failed"), [rows]);

  const headline = () => {
    const bits: React.ReactNode[] = [];
    if (queue.length)
      bits.push(
        <b key="q">
          {queue.length} {queue.length === 1 ? "person is" : "people are"} waiting
          for your decision
        </b>,
      );
    if (inFlight.length)
      bits.push(
        <span key="f">
          {inFlight.length} refund{inFlight.length > 1 ? "s are" : " is"} on the way
          to a customer
        </span>,
      );
    if (stuck.length)
      bits.push(
        <b key="s" className="rf-alarm">
          {stuck.length} failed at Cashfree with the wallet still debited
        </b>,
      );
    if (!bits.length) return <>Nothing is waiting and nothing is stuck.</>;
    return (
      <>
        {bits.map((b, i) => (
          <React.Fragment key={i}>
            {i > 0 && (i === bits.length - 1 ? ", and " : ", ")}
            {b}
          </React.Fragment>
        ))}
        .
      </>
    );
  };

  const approve = async (id: number) => {
    setActing(id); setErr(null);
    try {
      const r = await approveRefund(id);
      setFlash(
        r.gateway_error
          ? `Wallet debited but Cashfree did not accept it: ${r.gateway_error}. The reconciler retries against the same reference, so this cannot double-refund.`
          : `Approved. Cashfree says ${r.gateway_status ?? "pending"} — the customer sees it in 7–10 working days.`,
      );
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "could not approve");
    } finally {
      setActing(null); refreshQueue(); refreshList();
    }
  };

  const decline = async (id: number, why: string) => {
    setActing(id); setErr(null);
    try {
      await rejectRefund(id, why);
      setFlash("Declined. Nothing moved, and the customer keeps their balance.");
    } catch (e: any) {
      setErr(e?.response?.data?.detail ?? "could not decline");
    } finally {
      setActing(null); refreshQueue(); refreshList();
    }
  };

  const open = async (id: number) => {
    setCheckMsg(null);
    try { setAudit(await refundAudit(id)); } catch { setAudit([]); }
    try { setDetail(await getRefund(id)); }
    catch (e: any) { setErr(e?.response?.data?.detail ?? "could not load that refund"); }
  };

  // Same code path as the cron on the server, so the two cannot disagree.
  const checkNow = async (id: number) => {
    setChecking(true); setCheckMsg(null);
    try {
      const r = await refreshRefund(id);
      setCheckMsg(
        !r.checked ? `Already ${statusStory(r.status).label.toLowerCase()} — nothing to check.`
        : r.verdict === "succeeded" ? "Cashfree confirms the customer has been paid."
        : r.verdict === "failed" ? `Cashfree rejected it: ${r.detail ?? r.pg_status}. The wallet is still debited.`
        : r.verdict === "recreated" ? "It had never reached Cashfree — raised again against the same reference."
        : r.verdict === "error" ? `Could not reach Cashfree: ${r.detail}`
        : `Still with Cashfree (${r.pg_status ?? "pending"}). ONHOLD is normal when the merchant balance is empty; it is not a failure.`,
      );
      setDetail(await getRefund(id));
      refreshList();
    } catch (e: any) {
      setCheckMsg(e?.response?.data?.detail ?? "could not check with Cashfree");
    } finally { setChecking(false); }
  };

  const override = async (id: number, outcome: "succeeded" | "failed") => {
    try {
      await settleRefund(id, { outcome });
      setDetail(await getRefund(id));
      refreshList();
    } catch (e: any) {
      setCheckMsg(e?.response?.data?.detail ?? "could not record that");
    }
  };

  const TABS: Array<[Tab, string, number | null]> = [
    ["queue", "Needs you", queue.length],
    ["flight", "On the way", inFlight.length + stuck.length],
    ["history", "History", null],
    ["issue", "Issue a refund", null],
    ["settings", "Settings", null],
  ];

  return (
    <MaintenanceLayout>
      <div className="rf-page">
        <header className="rf-head">
          <h1>Refunds</h1>
          <p className="rf-head__state">{headline()}</p>
        </header>

        <nav className="rf-tabs">
          {TABS.map(([id, label, count]) => (
            <button key={id} className={tab === id ? "on" : ""} onClick={() => setTab(id)}>
              {label}
              {count ? <span className="rf-tabs__count">{count}</span> : null}
            </button>
          ))}
        </nav>

        {err && <div className="rf-err" onClick={() => setErr(null)}>{err}</div>}
        {flash && <div className="rf-flash" onClick={() => setFlash(null)}>{flash}</div>}

        {tab === "queue" &&
          (queue.length === 0 ? (
            <Empty
              icon="☑"
              title="Nobody is waiting"
              body={
                <>
                  When a customer asks for a refund in the app it appears here, and{" "}
                  {cfg?.alert_to || cfg?.reviewer_emails || "the reviewer"} gets an
                  email within ten minutes. Nothing moves until someone decides.
                </>
              }
            />
          ) : (
            queue.map((r) => (
              <ReviewCard
                key={r.id}
                r={r}
                mayAct={mayAct}
                busy={acting === r.id}
                onApprove={() => approve(r.id)}
                onDecline={(why) => decline(r.id, why)}
              />
            ))
          ))}

        {tab === "flight" && (
          <>
            {stuck.length > 0 && (
              <section className="rf-card rf-card--alarm">
                <h3>Needs rescuing</h3>
                <p className="rf-muted">
                  Cashfree rejected these and the wallet is still debited. The
                  customer has not been paid, and money is never put back
                  automatically — that is a person's decision.
                </p>
                {stuck.map((r) => (
                  <button key={r.id} className="rf-line" onClick={() => open(r.id)}>
                    <b>{r.who ?? `wallet ${r.user_wallet_id}`}</b>
                    <span>{inr(r.amount)}</span>
                    <span className="rf-muted">{r.failure_detail ?? "rejected"}</span>
                    <span className="rf-muted">{ago(r.created_at)}</span>
                  </button>
                ))}
              </section>
            )}
            {inFlight.length === 0 && stuck.length === 0 ? (
              <Empty
                icon="✈"
                title="Nothing in flight"
                body="No refund is currently with Cashfree waiting to settle."
              />
            ) : (
              inFlight.map((r) => (
                <article key={r.id} className="rf-card rf-flightcard">
                  <div className="rf-flightcard__top">
                    <div>
                      <b>{r.who ?? `wallet ${r.user_wallet_id}`}</b>
                      <span className="rf-muted"> · {r.phone} · raised {ago(r.created_at)}</span>
                    </div>
                    <div className="rf-flightcard__amt">{inr(r.amount)}</div>
                  </div>
                  <Journey status={r.status} raisedByUser={false} />
                  <p className="rf-muted">
                    Cashfree says {r.pg_status ?? "pending"}. Banks take 7–10 working
                    days; we ask again every ten minutes.
                  </p>
                  <div className="rf-actions">
                    <button onClick={() => open(r.id)}>Open</button>
                  </div>
                </article>
              ))
            )}
          </>
        )}

        {tab === "history" && (
          <HistoryTab rows={rows} onOpen={open} selected={detail?.id ?? null} />
        )}

        {tab === "issue" && (
          <IssueTab mayAct={mayAct} onDone={() => { refreshList(); refreshQueue(); }} />
        )}

        {tab === "settings" && (
          <SettingsTab cfg={cfg} mayAct={mayAct} onSaved={setCfg} />
        )}

        {detail && (
          <DetailDrawer
            d={detail}
            audit={audit}
            mayAct={mayAct}
            checking={checking}
            message={checkMsg}
            onCheck={() => checkNow(detail.id)}
            onOverride={(o) => override(detail.id, o)}
            onClose={() => setDetail(null)}
          />
        )}
      </div>
    </MaintenanceLayout>
  );
};

/* ---------------------------------------------------------------- history */

const HistoryTab: React.FC<{
  rows: RefundRow[];
  selected: number | null;
  onOpen: (id: number) => void;
}> = ({ rows, selected, onOpen }) => {
  const [q, setQ] = useState("");
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter((r) =>
      [r.who, r.phone, r.pg_refund_id, String(r.id), String(r.user_wallet_id)]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(t)),
    );
  }, [rows, q]);

  return (
    <section className="rf-card">
      <div className="rf-search">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by name, phone, reference or refund number"
        />
      </div>
      {shown.length === 0 ? (
        <Empty
          icon="⌕"
          title={rows.length ? "Nothing matches that" : "No refunds yet"}
          body={
            rows.length
              ? "Try a phone number, or the reference from a customer's email."
              : "Refunds raised in the app or issued here will be listed."
          }
        />
      ) : (
        <div className="rf-scroll">
          <table className="rf-table rf-table--click">
            <thead>
              <tr>
                <th>#</th><th>Who</th><th>Amount</th>
                <th>Where it stands</th><th>Raised</th><th>Settled</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr
                  key={r.id}
                  className={selected === r.id ? "rf-sel" : ""}
                  onClick={() => onOpen(r.id)}
                >
                  <td>{r.id}</td>
                  <td>
                    {r.who ?? `wallet ${r.user_wallet_id}`}
                    <span className="rf-muted"> {r.phone ?? ""}</span>
                  </td>
                  <td>
                    {inr(r.amount)}
                    {r.clawback_amount > 0 && (
                      <span className="rf-muted"> +{inr(r.clawback_amount)} cashback</span>
                    )}
                  </td>
                  <td><Badge status={r.status} /></td>
                  <td>{dt(r.created_at)}</td>
                  <td>{dt(r.settled_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

/* ------------------------------------------------------------------ issue */

const IssueTab: React.FC<{ mayAct: boolean; onDone: () => void }> = ({ mayAct, onDone }) => {
  const [q, setQ] = useState("");
  const [wallet, setWallet] = useState<WalletLookup | null>(null);
  const [orderId, setOrderId] = useState<number | null>(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("customer-request");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useNotice("error", null);
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState<SourceRefundResult | null>(null);

  const search = async () => {
    setErr(null); setDone(null); setWallet(null); setConfirming(false);
    if (q.trim().length < 3) {
      setErr("Type at least 3 characters — a phone number or a wallet id.");
      return;
    }
    setBusy(true);
    try {
      const w = await lookupWallet(q.trim());
      setWallet(w);
      setOrderId(w.suggested_order_id);
      setAmount(w.quote ? String(w.quote.refundable) : "");
    } catch (e: any) {
      setErr(e?.response?.data?.detail ?? "Nothing found for that.");
    } finally { setBusy(false); }
  };

  // Re-quoting on a different lot is a server concern -- the caps depend on the
  // wallet, not just the lot -- so this only moves the local cap.
  const pickLot = (l: RefundLot) => {
    if (!wallet) return;
    setOrderId(l.order_id);
    setAmount(
      Math.min(l.remaining, Math.max(0, wallet.cash_balance), Math.max(0, wallet.balance))
        .toFixed(2),
    );
  };

  const submit = async () => {
    if (!wallet || !orderId) return;
    setBusy(true); setErr(null);
    try {
      const res = await sourceRefund({
        wallet_id: wallet.wallet_id, order_id: orderId,
        amount: Number(amount), reason_code: reason,
        reason_note: note || undefined,
      });
      setDone(res); setWallet(null); setConfirming(false); onDone();
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setErr(
        typeof d === "string" ? d
        : d?.blocked_by ? d.blocked_by.map((b: any) => b.check).join("; ")
        : "The refund did not go through.",
      );
      setConfirming(false);
    } finally { setBusy(false); }
  };

  const lot = wallet?.lots.find((l) => l.order_id === orderId) ?? null;
  const blockers = wallet?.gate.filter((g) => !g.ok) ?? [];

  return (
    <>
      <section className="rf-card">
        <h3>Issue a refund on someone's behalf</h3>
        <p className="rf-muted">
          This skips the review step — you are the decision. A customer raising it
          in the app lands in <b>Needs you</b> instead.
        </p>
        <div className="rf-search">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="Phone number or wallet id"
          />
          <button onClick={search} disabled={busy}>{busy ? "…" : "Look up"}</button>
        </div>
        {err && <div className="rf-err">{err}</div>}
      </section>

      {done && (
        <section className={`rf-card ${done.gateway_error ? "rf-card--alarm" : "rf-card--ok"}`}>
          <h3>{done.gateway_error ? "Wallet debited — Cashfree has not accepted it" : "Refund sent"}</h3>
          <Figures
            rows={[
              ["Refunded", inr(done.amount)],
              ...(done.clawback > 0
                ? ([["Cashback forfeited", inr(done.clawback), "promo"]] as Array<
                    [string, React.ReactNode, string]
                  >)
                : []),
              ["Back to", done.cashfree?.upi_id || done.cashfree?.paid_by || "the original method"],
              ["Wallet now", inr(done.balance_after)],
              ["Our reference", done.pg_refund_id],
            ]}
          />
          <p className="rf-hint">
            {done.gateway_error ? (
              <>
                The wallet is debited on purpose, so the customer cannot spend money
                that is on its way back to them. The reconciler retries against the
                same reference, so this cannot become two refunds. Error:{" "}
                {done.gateway_error}
              </>
            ) : (
              <>
                The customer sees it in <b>7–10 working days</b> — tell them that,
                not “instantly”. Open it in History to copy a message for them.
              </>
            )}
          </p>
        </section>
      )}

      {wallet && (
        <>
          <section className="rf-card">
            <div className="rf-who">
              <div>
                <h3>{wallet.display_name || wallet.phone || `Wallet ${wallet.wallet_id}`}</h3>
                <span className="rf-muted">
                  {wallet.phone} · wallet {wallet.wallet_id} · {wallet.org_name ?? "no org"}
                </span>
              </div>
              <div className="rf-buckets">
                <div><span>Balance</span><b>{inr(wallet.balance)}</b></div>
                <div><span>Their money</span><b className="cash">{inr(wallet.cash_balance)}</b></div>
                <div><span>Cashback</span><b className="promo">{inr(wallet.promo_balance)}</b></div>
              </div>
            </div>
            {blockers.length > 0 ? (
              <div className="rf-flag bad">
                <b>Cannot refund this wallet yet.</b>
                <ul>
                  {blockers.map((b) => <li key={b.check}>{b.check} — {b.detail}</li>)}
                </ul>
              </div>
            ) : (
              <p className="rf-flag ok">✓ All checks pass — this wallet can be refunded.</p>
            )}
          </section>

          <section className="rf-card">
            <h3>Which recharge is the money coming out of?</h3>
            <p className="rf-muted">
              Money can only go back the way it came in, so a refund is always
              against one recharge. Greyed rows cannot be used.
            </p>
            <div className="rf-scroll">
              <table className="rf-table">
                <thead>
                  <tr>
                    <th></th><th>Paid</th><th>Amount</th><th>Spent</th>
                    <th>Left</th><th>Age</th><th>Paid by</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {wallet.lots.map((l) => {
                    const usable = l.in_window && l.remaining > 0 && l.looks_cashfree;
                    return (
                      <tr key={l.order_id} className={usable ? "" : "rf-dim"}>
                        <td>
                          <input
                            type="radio" name="lot" disabled={!usable}
                            checked={orderId === l.order_id}
                            onChange={() => pickLot(l)}
                          />
                        </td>
                        <td>{dt(l.created_at)}</td>
                        <td>{inr(l.amount)}</td>
                        <td>{inr(l.consumed)}</td>
                        <td><b>{inr(l.remaining)}</b></td>
                        <td>{l.age_days}d</td>
                        <td>{l.payment_group ?? "—"}{l.upi_id ? ` · ${l.upi_id}` : ""}</td>
                        <td className="rf-why">
                          {!l.in_window && "past the gateway's window"}
                          {l.in_window && l.remaining <= 0 && "fully spent"}
                          {l.in_window && l.remaining > 0 && !l.looks_cashfree && "not a Cashfree payment"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {wallet.quote && lot && (
            <section className="rf-card">
              <h3>How much</h3>
              <Figures
                rows={[
                  ["Refundable", inr(wallet.quote.refundable)],
                  ["Cashback forfeited", inr(wallet.quote.clawback), "promo"],
                  ["Wallet left at", inr(wallet.quote.balance_after)],
                ]}
              />
              <p className="rf-muted">Capped by {wallet.quote.limited_by}.</p>

              <div className="rf-form">
                <label>
                  Amount
                  <input
                    value={amount} onChange={(e) => setAmount(e.target.value)}
                    disabled={!mayAct} inputMode="decimal"
                  />
                </label>
                <label>
                  Reason
                  <select value={reason} onChange={(e) => setReason(e.target.value)} disabled={!mayAct}>
                    {REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </label>
                <label className="rf-note">
                  Note
                  <input
                    value={note} onChange={(e) => setNote(e.target.value)}
                    disabled={!mayAct} placeholder="optional, kept on the record"
                  />
                </label>
              </div>

              {!mayAct && <p className="rf-muted">Read-only — a super-admin issues refunds.</p>}

              {mayAct && !confirming && (
                <button
                  className="rf-primary"
                  disabled={!wallet.clear_to_refund || busy}
                  onClick={() => setConfirming(true)}
                >
                  {wallet.clear_to_refund ? "Refund…" : "Blocked — see the checks above"}
                </button>
              )}

              {mayAct && confirming && (
                <div className="rf-confirm">
                  <p>
                    Send <b>{inr(Number(amount))}</b> back to{" "}
                    <b>{lot.upi_id ?? lot.payment_group ?? "the original method"}</b>
                    {wallet.quote.clawback > 0 && (
                      <> and forfeit <b>{inr(wallet.quote.clawback)}</b> of cashback</>
                    )}
                    . The wallet is left at <b>{inr(wallet.quote.balance_after)}</b>.
                    This cannot be undone here.
                  </p>
                  <div className="rf-actions">
                    <button className="rf-danger" onClick={submit} disabled={busy}>
                      {busy ? "Sending…" : `Refund ${inr(Number(amount))} now`}
                    </button>
                    <button onClick={() => setConfirming(false)} disabled={busy}>
                      Keep the money in the wallet
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}
        </>
      )}
    </>
  );
};

/* --------------------------------------------------------------- settings */

const SettingsTab: React.FC<{
  cfg: RefundSettings | null;
  mayAct: boolean;
  onSaved: (c: RefundSettings) => void;
}> = ({ cfg, mayAct, onSaved }) => {
  const [draft, setDraft] = useState<Partial<RefundSettings>>({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useNotice("success", null);

  if (!cfg) return <Empty icon="⚙" title="Loading settings" body="One moment." />;

  const val = <K extends keyof RefundSettings>(k: K): any =>
    draft[k] !== undefined ? draft[k] : cfg[k];
  const set = (k: keyof RefundSettings, v: any) => setDraft((d) => ({ ...d, [k]: v }));

  // Only send what actually changed -- the API records who changed what, and a
  // full-object PUT would log every field as touched on every save.
  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const c = await putRefundSettings(draft);
      onSaved(c); setDraft({});
      setMsg("Saved. It applies immediately — no deploy.");
    } catch (e: any) {
      setMsg(e?.response?.data?.detail ?? "could not save");
    } finally { setSaving(false); }
  };

  const changed = Object.keys(draft).length;

  return (
    <>
      <section className="rf-card">
        <h3>How refunds behave</h3>
        <p className="rf-muted">
          All of this is policy, not code. Changing it here takes effect
          immediately — nothing needs deploying.
        </p>
        <div className="rf-form">
          <label>
            Refunds from the app
            <select
              value={String(val("self_serve_enabled"))} disabled={!mayAct}
              onChange={(e) => set("self_serve_enabled", e.target.value === "true")}
            >
              <option value="true">On</option>
              <option value="false">Off</option>
            </select>
          </label>
          <label>
            Customers can ask for (days)
            <input
              type="number" value={val("self_serve_window_days") ?? ""} disabled={!mayAct}
              onChange={(e) => set("self_serve_window_days", Number(e.target.value))}
            />
          </label>
          <label>
            Orgs allowed (comma separated ids)
            <input
              value={(val("self_serve_org_ids") ?? []).join(",")} disabled={!mayAct}
              onChange={(e) =>
                set("self_serve_org_ids",
                  e.target.value.split(",").map((x) => Number(x.trim())).filter((n) => !Number.isNaN(n)))
              }
            />
          </label>
        </div>
        <p className="rf-hint">
          Cashfree stops accepting refunds after {cfg.gateway_window_days} days. The
          gap between that and the {val("self_serve_window_days")}-day window above
          is how long you have to review a request before it becomes impossible to
          pay.
        </p>
      </section>

      <section className="rf-card">
        <h3>Who gets told</h3>
        <div className="rf-form">
          <label>
            Reviewer email(s)
            <input
              value={val("reviewer_emails") ?? ""} disabled={!mayAct}
              onChange={(e) => set("reviewer_emails", e.target.value)}
            />
          </label>
          <label>
            Alerts to
            <input
              value={val("alert_to") ?? ""} disabled={!mayAct}
              onChange={(e) => set("alert_to", e.target.value)}
            />
          </label>
          <label>
            Alerts from
            <input
              value={val("alert_from") ?? ""} disabled={!mayAct}
              onChange={(e) => set("alert_from", e.target.value)}
            />
          </label>
        </div>
        <p className="rf-hint">
          A customer raising a refund emails this address within ten minutes, and so
          does every outcome after it.
        </p>
      </section>

      <section className="rf-card">
        <h3>What the customer reads</h3>
        <label className="rf-field-block">
          <span>Terms shown before they submit — one per line</span>
          <textarea
            rows={5} disabled={!mayAct}
            value={(val("customer_terms") ?? []).join("\n")}
            onChange={(e) =>
              set("customer_terms", e.target.value.split("\n").map((l) => l.trim()).filter(Boolean))
            }
          />
        </label>
        <label className="rf-field-block">
          <span>Message after they submit</span>
          <textarea
            rows={2} disabled={!mayAct} value={val("submitted_message") ?? ""}
            onChange={(e) => set("submitted_message", e.target.value)}
          />
        </label>
        <label className="rf-field-block">
          <span>Notice on the recharge screen</span>
          <textarea
            rows={2} disabled={!mayAct} value={val("recharge_notice") ?? ""}
            onChange={(e) => set("recharge_notice", e.target.value)}
          />
        </label>
      </section>

      <section className="rf-card">
        {msg && <p className="rf-hint rf-hint--loud">{msg}</p>}
        {mayAct ? (
          <div className="rf-actions">
            <button className="rf-primary" disabled={saving || changed === 0} onClick={save}>
              {saving ? "Saving…" : changed === 0 ? "Nothing changed" : `Save ${changed} change${changed > 1 ? "s" : ""}`}
            </button>
            <button className="ghost" onClick={() => { setDraft({}); setMsg(null); }}>
              discard
            </button>
          </div>
        ) : (
          <p className="rf-muted">Read-only — a super-admin can change these.</p>
        )}
        <p className="rf-hint">
          Last changed {dt(cfg.updated_at)}{cfg.updated_by ? ` by ${cfg.updated_by}` : ""}.
        </p>
      </section>
    </>
  );
};

export default RefundConsole;
